import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Guid } from 'guid-typescript';
import { from, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { AuthSsoService } from '../services/auth/auth-sso.service';
import { KeycloakService } from '@core/services/auth/keycloak.service';

/**
 * Authentication Interceptor
 * Handles the logic to refresh tokens and adding authentication headers.
 * @param req - The request we are intercepting.
 * @param next - The next interceptor in the chain.
 */
export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const clientName = 'cs_chargingservices_frontend';
  const authService = inject(AuthSsoService);
  const activatedRoute = inject(ActivatedRoute);
  const keycloak = inject(KeycloakService);

  const excludedUrls = ['hereapi.com'];

  // Exclude these URLs from the interceptor. Passing it on to the next one.
  if (excludedUrls.some((url) => req.url.includes(url))) {
    return next(req);
  }

  function refreshTokenIfNotExpired(): Observable<boolean> {
    const authenticated = authService.isLoggedIn();
    const expired = authService.isKeycloakTokenExpired();

    if (authenticated && expired) {
      return from(authService.refreshToken()).pipe(map(() => true));
    }

    return of(false);
  }

  function addHeader(request: HttpRequest<unknown>, queryParams: Params) {
    const token = keycloak.getToken();

    let headers = request.headers
      .set('authorization', `Bearer ${token}`)
      .set('x-client', clientName)
      .set('X-Correlation-ID', Guid.create().toString());

    if (queryParams['mode'] === 'demo') {
      headers = headers.set('X-Demo-Mode', 'true');
    }

    return request.clone({ headers });
  }

  function getLatestQueryParams() {
    return activatedRoute.queryParams.pipe(take(1));
  }

  return refreshTokenIfNotExpired().pipe(
    switchMap(() => getLatestQueryParams()),
    switchMap((queryParams) => next(addHeader(req, queryParams))),
  );
};
